import { Head, Link, router, usePage } from '@inertiajs/react';

import { trackUmamiEvent } from '@/lib/analytics';
import type { NewsPost } from '@/pages/Admin/NewsEditor';

interface NewsPageProps {
  news: NewsPost[];
  [key: string]: unknown;
}

export default function News() {
  const { news } = usePage<NewsPageProps>().props;

  function handleDelete(post: NewsPost) {
    if (!window.confirm(`Eliminar a notícia "${post.title}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    trackUmamiEvent('admin_content_delete', { content_type: 'news' });
    router.delete(`/news/${post.id}`, { preserveScroll: true });
  }

  return (
    <>
      <Head title="Notícias — Administração" />
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-headline text-on-surface text-2xl font-bold">Notícias</h1>
          <Link
            href="/news/new"
            className="bg-primary hover:bg-primary-dark inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white transition-colors duration-200"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            Nova notícia
          </Link>
        </div>

        <div className="surface-card overflow-hidden">
          {news.length === 0 ? (
            <div className="text-on-surface-light flex flex-col items-center gap-2 py-12 text-center text-sm">
              <span className="material-symbols-outlined text-on-surface-light/40 text-3xl">
                newspaper
              </span>
              Nenhuma notícia publicada ainda.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-left">
                <thead>
                  <tr className="border-line border-b">
                    <th className="text-on-surface-light/70 px-4 py-3 text-xs font-bold tracking-wider uppercase">
                      Título
                    </th>
                    <th className="text-on-surface-light/70 px-4 py-3 text-xs font-bold tracking-wider uppercase">
                      Slug
                    </th>
                    <th className="text-on-surface-light/70 px-4 py-3 text-xs font-bold tracking-wider uppercase">
                      Publicado em
                    </th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {news.map((post) => (
                    <tr
                      key={post.id}
                      className="border-line hover:bg-background border-b transition-colors last:border-b-0"
                    >
                      <td className="text-on-surface px-4 py-3.5 text-sm font-semibold">
                        <span className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-on-surface-light/50 text-[16px]">
                            newspaper
                          </span>
                          {post.title}
                        </span>
                      </td>
                      <td className="text-on-surface-light px-4 py-3.5 text-sm">{post.slug}</td>
                      <td className="text-on-surface-light px-4 py-3.5 text-xs">
                        {post.publishedAt
                          ? new Date(post.publishedAt).toLocaleDateString('pt-PT', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            href={`/news/${post.id}/edit`}
                            aria-label="Editar notícia"
                            className="text-on-surface-light hover:bg-primary/10 hover:text-primary rounded-md p-1.5 transition-colors"
                          >
                            <span className="material-symbols-outlined text-[18px]">edit</span>
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleDelete(post)}
                            aria-label="Eliminar notícia"
                            className="text-on-surface-light hover:bg-error/10 hover:text-error rounded-md p-1.5 transition-colors"
                          >
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
